import { useEffect, useState, useRef } from "react"
import { useController } from "react-hook-form"
import IAEmail from "./types"
import isEmail from "@mobi/utils/validators/email"
import mergeDeep from "@mobi/utils/object/mergeDeep"

const DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "yahoo.com.br",
  "outlook.com",
  "icloud.com",
  "live.com",
  "yahoo.com",
  "bol.com.br",
  "uol.com.br",
  "terra.com.br",
  "hotmail.com.br",
  "outlook.com.br",
  "msn.com",
  "ig.com.br",
  "globo.com",
  "mail.com",
  "protonmail.com",
  "rocketmail.com",
  "ibest.com.br",
  "zipmail.com.br",
]

const useEmail = ({
  control,
  name,
  openOnFocus = true,
  required = true,
  validators: { rules: propRules = {}, messages: propMessages = {} } = {},
  maxLength,
  ...props
}: IAEmail) => {
  const fieldId = `ds-field-${name}`

  const [errorMessage, setErrorMessage] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const [options, setOptions] = useState([])
  const [isFirstListElement, setIsFirstListElement] = useState(true)
  const [isListOpen, setIsListOpen] = useState(false)

  const ref: any = useRef()

  const DEFAULT_MESSAGES = {
    required: "campo obrigatório",
    email: "Email inválido",
    specialCharacters: "Não use caracteres especiais: *, #, $",
    maxLength: `Limite máximo de ${maxLength} caracteres`,
  }

  const DEFAULT_RULES = {
    validate: {
      email: isEmail,
      specialCharacters: (email) =>
        email.match(/[~!#$%^&*()+=`{}[\]|\\:;'<>,/?]/gi),
      maxLength: (email) => {
        if (maxLength) return email.length < maxLength
      },
    },
  }

  const rules = mergeDeep(DEFAULT_RULES, propRules, { required })
  const customMessages = mergeDeep(DEFAULT_MESSAGES, propMessages)

  const { field, fieldState } = useController({ name, control, rules })
  const { error, isDirty, invalid, isTouched } = fieldState

  const hasInteraction = isDirty || isTouched
  const hasErrorMessage = hasInteraction && !!errorMessage
  const isEmptyList = options && options.length === 0
  const isInvalid = isTouched && invalid && isDirty
  const { allowSuggestions = true } = props

  const filterOptions = ({ options, search }) => {
    if (search.includes("@")) {
      const email = search.split("@")[0]
      const domain = search.split("@")[1]

      if (!email.length) return []

      const filteredOptions = options.filter((option) =>
        option.toString().toLowerCase().includes(domain.toLowerCase())
      )

      return filteredOptions.map((option) => `${email}@${option}`)
    }
  }

  const onCustomChange = (e) => {
    const search = e?.target?.value

    if (allowSuggestions) {
      const arrayEmails = props?.emailsSuggestion || DOMAINS
      const results = filterOptions({ options: arrayEmails, search })
      setOptions(results)
    }

    field.onChange(search)
    props.onChange && props.onChange(search)
  }

  const onSelectItem = (option) => {
    setOptions([])
    setErrorMessage("")

    field.onChange(option)
    props.onChange && props.onChange(option)
    props.onSelectItem && props.onSelectItem(option)
  }

  const onFocus = (e) => {
    if (allowSuggestions) {
      setIsFocused(true)
    }

    props?.onFocus && props?.onFocus(e)
  }

  const onBlur = (e) => {
    if (allowSuggestions) {
      setIsFocused(false)
      if (e.relatedTarget && e.relatedTarget.className.includes(`result-list`))
        return false

      if (openOnFocus) {
        setOptions([])
      }
    }

    if (!field.value) {
      if (required) {
        setErrorMessage(customMessages.required)
      }
    }

    field.onBlur()
    props.onBlur && props.onBlur(e)
  }

  const onKeyDown = (e) => {
    const key = e.key
    const listbox = document.querySelector(`#results-${fieldId}`)
    const field = document.querySelector(`#${fieldId}`) as HTMLElement
    const activeElementID = field.getAttribute("aria-activedescendant")
    const activeElement = listbox?.querySelector(`#${activeElementID}`)
    const activeCssClass = "ds-email__item--active"

    if (key === "ArrowDown" || key === "ArrowUp") {
      const isNextOrPrevious =
        (activeElement && activeElement.nextSibling) ||
        (activeElement && activeElement.previousSibling)

      const nextElement = (
        isNextOrPrevious && !isFirstListElement
          ? activeElement[
              key === "ArrowDown" ? "nextSibling" : "previousSibling"
            ]
          : activeElement
      ) as any

      if (nextElement) {
        const characters = [...listbox.children]
        field.setAttribute("aria-activedescendant", nextElement.id)
        characters.forEach((element: any) => {
          element.classList.remove(activeCssClass)
          element.setAttribute("aria-selected", false)
        })
        nextElement.setAttribute("aria-selected", true)
        nextElement.classList.add(activeCssClass)
        document.querySelector(`#option-list-${fieldId}`).scrollTop =
          nextElement.offsetTop - 120
      }
      setIsFirstListElement(false)
    } else if (key === "Enter") {
      if (activeElement) {
        e.preventDefault()
        const selectedItemOnEnter = activeElement.id.split("_").pop()
        field.setAttribute("aria-activedescendant", `option-${fieldId}_0`)
        onSelectItem(options[selectedItemOnEnter])
      }
    }
  }

  useEffect(() => {
    if (allowSuggestions && options && options.length) {
      setIsListOpen(true)
    } else {
      setIsListOpen(false)
    }
  }, [options])

  useEffect(() => {
    if (isListOpen && ref.current !== isListOpen) {
      props.onShowList && props.onShowList()
    }
    ref.current = isListOpen
  }, [isListOpen])

  useEffect(() => {
    if (!isDirty) return
    if (isInvalid) {
      setErrorMessage(customMessages[error?.type])
      return
    }
    const hasRequiredError = required && !field.value
    if (hasRequiredError) {
      setErrorMessage(customMessages.required)
      return
    }
    setErrorMessage("")
  }, [error])

  return {
    customMessages,
    error,
    errorMessage,
    field,
    fieldId,
    fieldState,
    hasErrorMessage,
    isEmptyList,
    isFocused,
    isInvalid,
    onBlur,
    onCustomChange,
    onFocus,
    onKeyDown,
    onSelectItem,
    options,
    required,
    isDirty,
  }
}

export default useEmail
